<template>
  <NuxtLayout name="sticky" shamrock>
    <template #header>
      <ScratchcardHeader with-fly-out />
    </template>

    <ScratchcardQuantity data-containerid="scratch_editor" />
    <ScratchcardSoldOutModal
      :is-visible="
        (!gameLoading && gameNotAvailable) || isReservationNotPossible
      "
    />
    <TheMetaLinks class="mt-lg" />

    <template #navbar>
      <LcButton class="w-full" :disabled="loading" @click="checkout">
        {{ $t('scratchcard.navbar.label') }}
      </LcButton>
    </template>

    <CheckoutRestrictions
      v-if="restrictions"
      v-bind="{ grossTotal, balance, restrictions }"
    />

    <ComplianceLocationPermissionModal
      :is-visible="showLocationPermissionModal"
      tracking-action="checkout"
      @cancel="showLocationPermissionModal = false"
    />

    <AccountVerificationModalControl
      :is-visible="showIsNotVerifiedModal"
      @close="showIsNotVerifiedModal = false"
    />
  </NuxtLayout>
</template>

<script lang="ts" setup>
import type { ApolloError } from '@apollo/client'
import type { GraphQLFormattedError } from 'graphql'

import { scratchcard } from '@lottocom/price-calculation'

import {
  KnownProductType,
  type Restrictions,
} from '~/@types/generated/backend/graphql-schema-types'
import Money from '~/models/Money'

import ScratchcardPriceBreakdown = scratchcard.ScratchcardPriceBreakdown

const isNotFound = ({ message }: GraphQLFormattedError) =>
  message === 'validation.game.notfound'

const isUnavailableGameError = ({ message }: GraphQLFormattedError) =>
  message.includes('unavailable.game.')

definePageMeta({
  layout: false,
  middleware: ['age-verification', 'self-excluded'],
  tracking: { name: 'ScratchcardEditor' },
  validate: () => useIsScratchcardEnabled(),
})

const nuxt = useNuxtApp()
const { t } = useI18n()
const { addToastError } = useToaster()
const { log } = useDatadog()
const { isLoggedIn } = useAuth()
const { logTrackingError } = useTrackingErrorLog()
const { createECommerceEventData } = useTrackingData()
const {
  pushAddToCartEvent,
  pushBeginCheckoutEvent,
  pushRemoveFromCartEvent,
  pushViewItemEvent,
} = useTracking()

const licensedTerritory = useLicensedTerritory({ raw: true })
const slug = useRoute().params.slug.toString()

const {
  loading: gameLoading,
  onError,
  onResult,
} = useScratchcardGameQuery(
  { licensedTerritory, slug },
  { fetchPolicy: 'network-only' },
)

const { game, priceList } = useScratchcardGame()

const store = useScratchcardStore()
const { currentSlug, selectedQuantity } = storeToRefs(store)
const { currentPrice, setCurrentSlug, setSelectedQuantity } = store

const { canCheckGeolocation } = useCompliance()

const { setProductId } = useCachedProduct()

const grossTotal = computed(
  () => currentPrice(priceList.value!)?.grossTotal ?? new Money(0),
)

const available = computed(() => game.value?.available)

const gameNotAvailable = computed(
  () => !game.value?.enabled || (available.value ?? 0) <= 0,
)

const balance = ref(new Money(0))

const isReservationNotPossible = ref(false)

const restrictions = ref<Maybe<Restrictions>>(null)

const showIsNotVerifiedModal = ref(false)

const showLocationPermissionModal = ref(false)

const { loading, mutate } = useReserveScratchcardsMutation()

onResult(({ data: { scratchcardGame } }) => {
  if (
    scratchcardGame.available < selectedQuantity.value &&
    !import.meta.server
  ) {
    setSelectedQuantity(scratchcardGame.available || MIN_SELECTED_QUANTITY)
  }
})

const checkout = async () => {
  await trackAddToCartEvent(true)
  await trackBeginCheckoutEvent()

  if (!isLoggedIn.value) return redirectToLogin()

  const { result: accountResult } = useAccountQuery()
  if (accountResult.value?.account)
    balance.value = new Money(accountResult.value.account.balance.value / 100)

  try {
    /*
     * (TODO) handle restrictions after Vue3 release
     * https://lottocom.atlassian.net/browse/MIRA-7804 needed
     * see https://lottocom.atlassian.net/browse/SF-4289
     */
    const result = await mutate({
      input: {
        gameSlug: slug,
        licensedTerritory,
        quantity: selectedQuantity.value,
      },
    })
    if (
      !result?.data?.reserveScratchcards.reservationId &&
      !result?.data?.reserveScratchcards.restrictions
    )
      return
    if (result.data.reserveScratchcards.restrictions) {
      restrictions.value = result.data.reserveScratchcards.restrictions
      return
    }

    setProductId(result!.data!.reserveScratchcards.reservationId!)
  } catch (error) {
    // (TODO) will be part of the restrictions (see above)
    if (
      isSpecificGraphQLError(error, {
        matchMessage: 'validation.player.invalidstatus',
      })
    ) {
      showIsNotVerifiedModal.value = true
      return
    }

    return handleError(error)
  }

  const canCheckLocation = await canCheckGeolocation()
  showLocationPermissionModal.value = !canCheckLocation
  if (!showLocationPermissionModal.value) {
    await navigateToCheckout()
  }
}

const handleError = (error: unknown) => {
  const { graphQLErrors } = error as ApolloError

  if (graphQLErrors?.length && !!graphQLErrors?.find(isUnavailableGameError)) {
    isReservationNotPossible.value = true
    return
  }

  addToastError(
    {
      error,
      prefix: 'scratchcard',
      tracking: {
        action: 'checkout',
        topicKey: 'navigate_to_scratchcard_checkout_error',
      },
    },
    {
      fallback: {
        message: t('global.error'),
        type: 'danger',
      },
    },
  )

  log('error', {
    component: 'scratchcard/[slug]/index.vue',
    error,
    message: `Reservation of ${slug} with quantity of ${selectedQuantity.value} failed.`,
  })
}

const navigateToCheckout = () =>
  navigateTo(
    {
      path: '/checkout',
      query: { p: slug },
    },
    {},
  )

const redirectToLogin = () =>
  navigateTo(
    createRedirectLocation(
      {
        path: '/login',
        replace: true,
      },
      `/scratchcard/${slug}`,
    ),
  )

onError((error) => {
  if (error.graphQLErrors.find(isNotFound)) {
    nuxt.runWithContext(() => showError({ statusCode: 404 }))
  } else {
    addToastError({
      error,
      prefix: 'scratchcard',
      tracking: {
        action: 'checkout',
        topicKey: 'scratchcard_game_error',
      },
    })
    nuxt.runWithContext(() => navigateTo('/play-now'))
  }
})

const hasTrackedViewItemEvent = ref(false)

const generateEventData = async () =>
  await createECommerceEventData({
    currentPrice: currentPrice(priceList.value!) as ScratchcardPriceBreakdown,
    name: game.value!.name,
    priceList: priceList.value!,
    quantity: selectedQuantity.value,
    type: KnownProductType.Scratchcard,
  })

const trackAddToCartEvent = async (nextStep = false) => {
  try {
    pushAddToCartEvent(await generateEventData(), nextStep)
  } catch (err) {
    logTrackingError('add_to_cart', err)
  }
}

const trackBeginCheckoutEvent = async () => {
  try {
    pushBeginCheckoutEvent(await generateEventData())
  } catch (err) {
    logTrackingError('begin_checkout', err)
  }
}

const trackRemoveFromCartEvent = async () => {
  try {
    pushRemoveFromCartEvent(await generateEventData())
  } catch (err) {
    logTrackingError('remove_from_cart', err)
  }
}

const trackOnMount = async () => {
  if (gameNotAvailable.value) return
  if (hasTrackedViewItemEvent.value) return
  if (!currentPrice(priceList.value!)) return
  try {
    pushViewItemEvent(await generateEventData())
  } catch (err) {
    logTrackingError('view_item', err)
  }
  await trackAddToCartEvent()
  hasTrackedViewItemEvent.value = true
}

onBeforeMount(() => {
  if (slug === currentSlug.value) return
  setSelectedQuantity(1)
  setCurrentSlug(slug)
})

onMounted(trackOnMount)

watch([gameNotAvailable, currentPrice], trackOnMount)
watch(
  () => selectedQuantity.value,
  (newValue, oldValue) => {
    const diff = newValue - oldValue
    if (diff === 0) return
    if (diff > 0) {
      trackAddToCartEvent()
    } else {
      trackRemoveFromCartEvent()
    }
  },
)
</script>
